import React, { useState } from 'react';
import { CheckCircle2, Phone, Heart, Shield, ArrowRight, FileText, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';
import { images } from '../utils/images';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GetOnNdisPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', interests: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in your name and email.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, message: `[GET ON THE NDIS FREE ENQUIRY] ${form.message}`, honeypot: '' }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success("Thank you! We'll be in touch shortly to start your journey.");
      setForm({ name: '', email: '', phone: '', message: '', interests: '' });
    } catch {
      toast.error('Something went wrong. Please call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#81b956] focus:ring-2 focus:ring-[#81b956]/20 outline-none transition-all text-base";

  const steps = [
    {
      icon: Heart,
      title: "We Get to Know You",
      desc: "We start with a genuine conversation — no forms, no pressure. We want to understand your life, your challenges, and what matters most to you. In simple terms, we just want to hear your story."
    },
    {
      icon: FileText,
      title: "We Handle the Paperwork",
      desc: "Applying for the NDIS means filling out forms and gathering evidence. That can feel overwhelming, so we do the heavy lifting. We prepare your submission, collect the supporting documents, and make sure everything is in order — at no cost to you."
    },
    {
      icon: Shield,
      title: "We Submit & Advocate for You",
      desc: "Once your application is ready, we submit it on your behalf and follow up with the NDIA. If they need more information, we handle it. If they push back, we push harder. We don't stop until you get the answer you deserve."
    },
    {
      icon: Users,
      title: "We Walk the Journey With You",
      desc: "Whether it takes weeks or months, we're not going anywhere. We stay by your side through the entire process, keeping you informed every step of the way. You will never feel alone in this."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Get on the NDIS Free" description="Think you might be eligible for the NDIS? Life Care and Wellness will take all the necessary steps to make a submission on your behalf — completely free." />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <div className="inline-block mb-4">
                <span
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: '#81b956' }}
                  data-testid="free-badge"
                >
                  100% Free — No Hidden Costs
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
                data-testid="ndis-free-title"
              >
                Get on the NDIS — Free
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                <strong>Have you got support?</strong> You may be eligible for an NDIS Plan. We will take all the necessary steps to make a submission on your behalf — for free, and however long it takes. We will help you navigate the process and take the journey along with you.
              </p>

              <div
                className="p-6 rounded-xl mb-6"
                style={{ backgroundColor: '#81b956', color: 'white' }}
              >
                <p className="text-lg font-semibold mb-2">
                  Already have a plan but no Support Coordination?
                </p>
                <p className="text-base leading-relaxed">
                  Do you have a plan that has no support coordination included? If so, we will work at no cost and take you through to your next review. You shouldn't have to figure this out alone — and with us, you won't have to.
                </p>
              </div>

              <p
                className="text-base leading-relaxed italic mb-6"
                style={{ color: '#6c935e' }}
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+611300404229">
                  <Button
                    size="lg"
                    className="text-white hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#81b956' }}
                    data-testid="ndis-free-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us Now
                  </Button>
                </a>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2"
                  style={{ borderColor: '#81b956', color: '#81b956' }}
                  onClick={() => document.getElementById('ndis-free-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  data-testid="ndis-free-enquire-btn"
                >
                  Start Your Free Enquiry
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={images.about}
                alt="Life Care and Wellness helping with NDIS access"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who Is Eligible */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Who Can Access the NDIS?
              </h2>
              <p
                className="text-lg md:text-xl mb-4"
                style={{ color: '#5e5e5e' }}
              >
                The NDIS (National Disability Insurance Scheme) is a government-funded program designed to help Australians living with a disability get the support they need. In plain English — if you have a permanent disability that affects your daily life, you may be eligible for funded supports that can genuinely change your quality of life.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                "You are under 65 years of age",
                "You are an Australian citizen, permanent resident, or hold a Protected Special Category Visa",
                "You have a permanent and significant disability that affects your ability to take part in everyday activities",
                "You need support from others or specialist equipment to manage day-to-day life",
                "Early intervention support would help reduce the impact of your disability",
                "Not sure if you qualify? That's exactly what we're here for — we'll help you find out"
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-4 p-5 rounded-xl" style={{ backgroundColor: '#f8f9fa' }}>
                  <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#81b956' }} />
                  <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How We Help — Step by Step */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                How We Help You Get on the NDIS — Step by Step
              </h2>
              <p
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                Other providers might hand you a form and wish you luck. We do things differently. Here's what our free service actually looks like.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {steps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#81b956' }}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-3" style={{ color: '#587740' }}>
                        Step {i + 1}: {step.title}
                      </h3>
                      <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                        {step.desc}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Free */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#587740' }}
            >
              Why Do We Do This for Free?
            </h2>
            <p
              className="text-lg md:text-xl leading-relaxed mb-6"
              style={{ color: '#5e5e5e' }}
            >
              Because we believe that getting access to the NDIS shouldn't depend on whether you can afford to pay someone to help you apply. Too many people miss out on the support they desperately need simply because the process feels too hard or too confusing. That's not right — and we want to change that.
            </p>
            <p
              className="text-lg md:text-xl leading-relaxed mb-8"
              style={{ color: '#5e5e5e' }}
            >
              We also offer <strong>free consultations</strong> and a <strong>free action plan</strong> based on your current situation. Whether you're just starting out or already have a plan that isn't working for you, we'll sit down with you, review everything, and create a clear path forward — at no cost whatsoever.
            </p>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="ndis-free-form" className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: '#587740' }}
              >
                Start Your Free Enquiry
              </h2>
              <p className="text-lg" style={{ color: '#5e5e5e' }}>
                Fill in the form below and one of our team will be in touch to have a friendly, no-pressure chat about your situation. Everything is confidential.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 bg-white p-8 rounded-2xl shadow-lg">
              <div>
                <label className="block text-base font-semibold mb-2" style={{ color: '#587740' }}>
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input name="name" value={form.name} onChange={handleChange} required className={inputClass} placeholder="Your full name" data-testid="ndis-free-name" />
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#587740' }}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required className={inputClass} placeholder="your.email@example.com" data-testid="ndis-free-email" />
                </div>
                <div>
                  <label className="block text-base font-semibold mb-2" style={{ color: '#587740' }}>
                    Phone
                  </label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange} className={inputClass} placeholder="04XX XXX XXX" data-testid="ndis-free-phone" />
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold mb-2" style={{ color: '#587740' }}>
                  Tell Us About Your Situation
                </label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} className={inputClass + ' resize-none'} placeholder="What's going on in your life right now? What kind of support do you think you need?" data-testid="ndis-free-message" />
              </div>

              <div>
                <label className="block text-base font-semibold mb-2" style={{ color: '#587740' }}>
                  Your Individual Interests & Passions
                  <span className="text-sm font-normal block mt-1" style={{ color: '#999' }}>
                    We want to know you as a person — what makes you happy?
                  </span>
                </label>
                <textarea name="interests" value={form.interests} onChange={handleChange} rows={3} className={inputClass + ' resize-none'} placeholder="e.g. music, cooking, gardening, sports, animals, art..." data-testid="ndis-free-interests" />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full text-white text-base py-6 hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: '#81b956' }}
                data-testid="ndis-free-submit-btn"
              >
                {loading ? 'Sending...' : 'Submit — It\'s 100% Free'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              You Deserve Support. Let Us Help You Get It.
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Whether you're applying for the NDIS for the first time or you have a plan that isn't working for you, we're here — and it won't cost you a cent. Free consultation. Free action plan. Free submission. That's our promise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+611300404229">
                <Button
                  size="lg"
                  className="text-base px-8 py-6"
                  style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us: 1300 404 229
                </Button>
              </a>
              <a href="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                >
                  Contact Us Online
                </Button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default GetOnNdisPage;
